import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { Box, Button, IconButton, Stack } from "@mui/material";
import type { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { AssetCount } from "../asset-count";
import { AssetGrid } from "../asset-grid";
import type { ContentLibraryState } from "../reducer/reducer";
import type { Asset } from "../server/digital-asset-service";
import type { Folder } from "../server/folder-service";
import { ROOT_ID } from "../utils";

type ContentLibraryAssetsProps = {
  filteredData: Asset[];
  state: ContentLibraryState;
  slots: {
    breadcrumb: ReactElement;
    filter: ReactElement;
    contentTypeChips: ReactElement;
    searchField: ReactElement;
    tagChips: ReactElement;
  };
  designer?: string | null;
  onSelectAsset: (asset: Asset) => void;
  onCreateFolder: () => void;
  filteredFolders: Folder[];
  onArchiveFolder: () => void;
};

export const ContentLibraryAssets: FC<ContentLibraryAssetsProps> = ({
  filteredData,
  state,
  slots,
  designer,
  onSelectAsset,
  onCreateFolder,
  filteredFolders,
  onArchiveFolder,
}) => {
  const { t } = useTranslation();

  const inRootFolder = !state.selectedTreeItemId || state.selectedTreeItemId === ROOT_ID;
  return (
    <Stack>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        {slots.breadcrumb}
        <Stack direction="row" alignItems="center" gap={1}>
          {!inRootFolder && (
            <Button variant="text" color="primary" data-analytics-id="content-library-move-folder">
              {t("Move folder")}
            </Button>
          )}
          <Button
            variant="text"
            color="primary"
            data-analytics-id="content-library-create-sub-folder"
            data-testid="content-library-create-sub-folder"
            onClick={onCreateFolder}
          >
            {t("Create sub-folder")}
          </Button>
          {!inRootFolder && (
            <IconButton
              data-testid="archive-folder"
              data-analytics-id="content-library-archive-folder"
              aria-label="Archive folder"
              onClick={onArchiveFolder}
            >
              <DeleteOutline color="error" sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Stack>
      </Box>
      <Stack direction="row" justifyContent="space-between" pb={2}>
        <Box flexGrow={1} mr={2}>
          {slots.searchField}
        </Box>
        {slots.filter}
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <AssetCount assets={filteredData} folders={filteredFolders} searchText={state.searchText} />
      </Stack>
      <Stack gap={1} sx={{ mb: 2 }}>
        {slots.contentTypeChips}
        {slots.tagChips}
      </Stack>
      <AssetGrid
        assets={filteredData}
        onClick={onSelectAsset}
        designer={designer}
        folders={filteredFolders}
        showFolders={state.appliedTags.length === 0 && state.appliedContentTypes.length === 0}
      />
    </Stack>
  );
};
